import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Account } from '@feathr/blackbox';
import { AvatarNameCell, Skeleton } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

export interface ICsmCellProps {
  original: Account;
}

// Width of the loading placeholder's text line, sized to a typical full name.
const SKELETON_WIDTH = 120;

/*
 * AvatarV2 tints an initials avatar from the placeholder's colour and falls back to red when none
 * is given — which reads as an error next to a person's name. Cyan is the colour AvatarV2 uses for
 * a user with no picture, so a CSM without a photo matches the rest of the app.
 */
const AVATAR_PLACEHOLDER = { color_name: 'cyan' };

/**
 * Account manager for the accounts table. Resolves the row's `csm` id to a user and renders their
 * picture beside their name.
 *
 * The user is fetched per row with an `only` projection, which keeps the payload small but leaves
 * the model partially fetched — so never save it from here. `getHeaders(false)` drops the
 * account-scoped header because a CSM is not a member of the account being listed.
 */
function CsmCell({ original }: Readonly<ICsmCellProps>): JSX.Element {
  const { Users } = useStore();

  const accountManager = original.get('csm');
  if (!accountManager) {
    return <AvatarNameCell avatarName={''} primary={''} />;
  }

  const user = Users.get(
    accountManager,
    undefined,
    { only: ['email', 'first_name', 'id', 'last_name', 'name', 'picture'] },
    { fetchOptions: { headers: Users.getHeaders(false) } },
  );

  if (user.isPending) {
    return <Skeleton prefix={true} width={SKELETON_WIDTH} />;
  }

  return (
    <AvatarNameCell
      avatarImage={user.get('picture')}
      avatarName={user.name}
      avatarPlaceholder={AVATAR_PLACEHOLDER}
      primary={user.name}
    />
  );
}

export default observer(CsmCell);
